/* istanbul ignore file */

import { createTheme, responsiveFontSizes, type Theme, type ThemeOptions } from '@mui/material/styles'
import {
  BRAND_NAVY,
  BRAND_NAVY_DARK,
  BRAND_NAVY_LIGHT,
  BRAND_RED,
  CREAM,
  CHROME_CONTRAST,
  CHROME_GAP,
  CHROME_RADIUS,
  DARK_ACCENT,
  DARK_CHROME,
  DARK_PAGE_BG,
  DARK_PAPER,
  DARK_STATUS,
  DARK_SUBTLE,
  DARK_TEXT_PRIMARY,
  DARK_TEXT_SECONDARY,
  GRAYBOOK_GOLD,
  GRAYBOOK_GOLD_HOVER,
  LIGHT_CHROME,
  LIGHT_STATUS,
  PAGE_BG,
  PAPER,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
  TEXT_SECONDARY,
  TOUCH_MIN,
} from './theme.tokens'

const dialogSafeAreaEdge = {
  boxSizing: 'border-box' as const,
  '&::before': {
    content: '""',
    display: 'block',
    flexShrink: 0,
    height: SAFE_AREA_TOP,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    display: 'block',
    flexShrink: 0,
    height: SAFE_AREA_BOTTOM,
    pointerEvents: 'none',
  },
}

export { PALETTE_TOKENS } from './theme.tokens'

const lightPalette = {
  mode: 'light' as const,
  contrastThreshold: 4.5,
  primary: {
    main: BRAND_NAVY,
    light: BRAND_NAVY_LIGHT,
    dark: BRAND_NAVY_DARK,
    contrastText: PAPER,
  },
  secondary: {
    main: BRAND_RED,
    contrastText: PAPER,
  },
  error: { main: '#c62828' },
  warning: { main: '#ed6c02' },
  info: { main: '#0277bd' },
  success: { main: '#2e7d32' },
  background: {
    default: PAGE_BG,
    paper: PAPER,
    subtle: CREAM,
  },
  text: {
    primary: BRAND_NAVY,
    secondary: TEXT_SECONDARY,
  },
}

const darkPalette = {
  mode: 'dark' as const,
  contrastThreshold: 4.5,
  primary: {
    main: DARK_ACCENT,
    light: BRAND_NAVY_LIGHT,
    dark: BRAND_NAVY,
    contrastText: DARK_PAGE_BG,
  },
  secondary: {
    main: BRAND_RED,
    contrastText: PAPER,
  },
  error: { main: '#ff8a80' },
  warning: { main: '#ffb74d' },
  info: { main: '#4fc3f7' },
  success: { main: '#81c784' },
  background: {
    default: DARK_PAGE_BG,
    paper: DARK_PAPER,
    subtle: DARK_SUBTLE,
  },
  text: {
    primary: DARK_TEXT_PRIMARY,
    secondary: DARK_TEXT_SECONDARY,
  },
}

const typography: ThemeOptions['typography'] = {
  fontFamily: 'Outfit, sans-serif',
  fontSize: 16,
  h1: { fontSize: '2.5rem', fontWeight: 500, lineHeight: 1.2 },
  h2: { fontSize: '2rem', fontWeight: 500, lineHeight: 1.3 },
  h3: { fontSize: '1.75rem', fontWeight: 500, lineHeight: 1.35 },
  h4: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.4 },
  h5: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.45 },
  h6: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.6 },
  subtitle1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.75 },
  subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.57 },
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.43 },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.75,
    textTransform: 'none',
  },
  caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.66 },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 2.66,
    textTransform: 'uppercase',
  },
}

const createComponents = (): ThemeOptions['components'] => ({
      MuiCssBaseline: {
        styleOverrides: {
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
            },
          },
          '@media (max-width: 599px)': {
            '.tsqd-parent-container': {
              display: 'none !important',
            },
          },
          form: {
            touchAction: 'auto',
          },
          '@media (pointer: coarse)': {
            'input, textarea, select': {
              fontSize: '16px !important',
              touchAction: 'auto',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // Let taps hit the real input. A label click that JS-focuses
            // the field is treated as programmatic on iOS and the keyboard
            // stays down.
            pointerEvents: 'none',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            touchAction: 'auto',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: theme.shape.borderRadius,
            textTransform: 'none',
            [theme.breakpoints.down('sm')]: {
              minHeight: TOUCH_MIN,
            },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            [theme.breakpoints.down('sm')]: {
              minWidth: TOUCH_MIN,
              minHeight: TOUCH_MIN,
            },
          }),
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: theme.shape.borderRadius,
            '&.MuiAppBar-root': {
              borderRadius: 12,
              [theme.breakpoints.down('sm')]: {
                borderRadius: 0,
              },
            },
          }),
        },
      },
      MuiDialog: {
        defaultProps: {
          fullWidth: true,
        },
        styleOverrides: {
          paper: ({ theme }: { theme: Theme }) => ({
            borderRadius: theme.shape.borderRadius,
            [theme.breakpoints.down('sm')]: {
              marginLeft: `${CHROME_GAP}px`,
              marginRight: `${CHROME_GAP}px`,
              marginTop: theme.spacing(3),
              marginBottom: theme.spacing(3),
              width: `calc(100% - ${CHROME_GAP * 2}px)`,
              maxHeight: 'calc(100% - 48px)',
              borderRadius: `${CHROME_RADIUS}px`,
              overflow: 'hidden',
              '&::before, &::after': { display: 'none' },
            },
          }),
          paperFullScreen: {
            margin: 0,
            width: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            borderRadius: 0,
            ...dialogSafeAreaEdge,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            [theme.breakpoints.down('sm')]: {
              paddingLeft: theme.spacing(2.5),
              paddingRight: theme.spacing(2.5),
              paddingTop: theme.spacing(2.5),
              fontSize: 18,
              fontWeight: 700,
            },
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            [theme.breakpoints.down('sm')]: {
              paddingLeft: theme.spacing(2.5),
              paddingRight: theme.spacing(2.5),
            },
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            [theme.breakpoints.down('sm')]: {
              paddingLeft: theme.spacing(2.5),
              paddingRight: theme.spacing(2.5),
              paddingBottom: theme.spacing(2.5),
              gap: theme.spacing(1),
              flexWrap: 'wrap',
            },
          }),
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.custom.chrome,
            color: theme.custom.chromeContrast,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.16)',
            [theme.breakpoints.down('sm')]: {
              borderRadius: 0,
              boxShadow: 'none',
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            minHeight: TOUCH_MIN,
            [theme.breakpoints.down('sm')]: {
              minHeight: TOUCH_MIN,
            },
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            textTransform: 'none',
            [theme.breakpoints.down('sm')]: {
              minHeight: TOUCH_MIN,
              minWidth: TOUCH_MIN,
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: theme.shape.borderRadius,
            height: 20,
            fontSize: '0.68rem',
            fontWeight: 600,
          }),
          label: {
            paddingLeft: 5,
            paddingRight: 5,
          },
          sizeSmall: {
            height: 18,
            fontSize: '0.65rem',
          },
          icon: {
            fontSize: '0.875rem',
            marginLeft: 4,
            marginRight: -2,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.background.subtle,
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: theme.shape.borderRadius,
          }),
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            minHeight: TOUCH_MIN + 12,
            borderTop: 'none',
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: TOUCH_MIN,
            minHeight: TOUCH_MIN,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            '&.Mui-selected': {
              border: `2px solid ${theme.custom.chrome}`,
              borderRadius: theme.shape.borderRadius,
              color: theme.custom.chrome,
              backgroundColor: theme.palette.background.subtle,
            },
          }),
        },
      },
})

export const createAppTheme = (mode: 'light' | 'dark') =>
  responsiveFontSizes(
    createTheme({
      palette: mode === 'dark' ? darkPalette : lightPalette,
      typography,
      spacing: 8,
      shape: {
        borderRadius: 8,
      },
      custom: {
        cream: CREAM,
        authBackground: '/photos/auth-background.png',
        chrome: mode === 'dark' ? DARK_CHROME : LIGHT_CHROME,
        chromeContrast: CHROME_CONTRAST,
        gold: GRAYBOOK_GOLD,
        goldHover: GRAYBOOK_GOLD_HOVER,
        status: mode === 'dark' ? DARK_STATUS : LIGHT_STATUS,
      },
      components: createComponents(),
    }),
  )

export const theme = createAppTheme('light')

export const authEmailInputProps = {
  inputMode: 'email' as const,
  autoCapitalize: 'none',
  autoCorrect: 'off',
  spellCheck: false,
  enterKeyHint: 'next',
}

export const authPasswordInputProps = {
  enterKeyHint: 'go',
  autoCapitalize: 'off',
  autoCorrect: 'off',
  spellCheck: false,
}

export const authTextFieldStyle = {
  backgroundColor: 'background.paper',
  borderRadius: theme.shape.borderRadius,
  '& .MuiOutlinedInput-root': {
    minHeight: TOUCH_MIN,
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
      pointerEvents: 'none',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'text.primary',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'text.secondary',
  },
  '& .MuiInputBase-input': {
    color: 'text.primary',
    fontSize: 16,
    WebkitUserSelect: 'text',
    userSelect: 'text',
  },
}

export default theme
